import { textColorForFill } from "./flowModel";
import { layoutFlow, textWidth } from "./flowLayout";
import { parseNoteBlocks } from "./flowNoteMarkup";

function round(value) {
  return Math.round(Number(value) * 10) / 10;
}

function escapeXml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const TEXT_LOCK = "text-indent:0;letter-spacing:0;word-spacing:0";
const NOTE_BODY_SIZE = 13;
const NOTE_LINE = 18;
const NOTE_PAD_X = 10;
const NOTE_PAD_TOP = 8;

function runFont(run, size) {
  const weight = run.bold ? "700" : "400";
  const style = run.italic ? "italic" : "normal";
  return `${style} ${weight} ${size}px "Segoe UI", system-ui, sans-serif`;
}

function runsMarkup(runs) {
  return runs.map((run) => {
    if (!run.text) return "";
    const attrs = [
      run.bold ? 'font-weight="700"' : "",
      run.italic ? 'font-style="italic"' : "",
      run.underline ? 'text-decoration="underline"' : "",
    ].filter(Boolean);
    const text = escapeXml(run.text);
    return attrs.length ? `<tspan ${attrs.join(" ")}>${text}</tspan>` : text;
  }).join("");
}

function wrapStyledBlocks(blocks, maxWidth, size) {
  const lines = [];
  const pushLine = (line) => {
    if (lines.length < 48) lines.push(line);
  };
  blocks.forEach((block) => {
    if (lines.length >= 48) return;
    if (!block.length) {
      pushLine([]);
      return;
    }
    let line = [];
    let width = 0;
    block.forEach((run) => {
      String(run.text || "").split(/(\s+)/).forEach((bit) => {
        if (!bit || lines.length >= 48) return;
        const token = { ...run, text: bit };
        const measured = textWidth(bit, runFont(token, size));
        const space = /^\s+$/.test(bit);
        if (!space && width + measured > maxWidth && line.some((part) => part.text.trim())) {
          pushLine(line);
          line = [];
          width = 0;
        }
        if (space && !line.length) return;
        if (lines.length >= 48) return;
        line.push(token);
        width += measured;
      });
    });
    if (line.length) pushLine(line);
  });
  return lines;
}

function noteMarkup(note, clipId) {
  const sections = note.sections || [];
  const count = Math.max(1, sections.length);
  const flow = note.flow === "horizontal" ? "horizontal" : "vertical";
  const design = ["plain", "sidebar", "outline", "note"].includes(note.design) ? note.design : "card";
  const chunks = [];
  sections.forEach((section, index) => {
    const colW = flow === "horizontal" ? note.w / count : note.w;
    const colH = flow === "vertical" ? note.h / count : note.h;
    const sx = note.x + (flow === "horizontal" ? colW * index : 0);
    const sy = note.y + (flow === "vertical" ? colH * index : 0);
    const headerH = Math.min(34, Math.max(22, colH * 0.34));
    const headerFill = section.headerFill || "#6b7280";
    const bodyFill = section.bodyFill || "#ffffff";
    const paper = design === "note" ? headerFill : bodyFill;
    const inset = design === "sidebar" ? 8 : 0;
    if (design === "card") {
      chunks.push(`<rect x="${round(sx)}" y="${round(sy)}" width="${round(colW)}" height="${round(headerH)}" fill="${escapeXml(headerFill)}"/>`);
      chunks.push(`<rect x="${round(sx)}" y="${round(sy + headerH)}" width="${round(colW)}" height="${round(Math.max(0, colH - headerH))}" fill="${escapeXml(bodyFill)}"/>`);
    } else if (design === "sidebar") {
      chunks.push(`<rect x="${round(sx)}" y="${round(sy)}" width="${round(colW)}" height="${round(colH)}" fill="${escapeXml(bodyFill)}"/>`);
      chunks.push(`<rect x="${round(sx)}" y="${round(sy)}" width="8" height="${round(colH)}" fill="${escapeXml(headerFill)}"/>`);
    } else {
      chunks.push(`<rect x="${round(sx)}" y="${round(sy)}" width="${round(colW)}" height="${round(colH)}" fill="${escapeXml(paper)}"/>`);
    }
    const titleFill = design === "outline" || design === "plain" ? headerFill : textColorForFill(design === "card" || design === "note" ? headerFill : paper);
    const padX = NOTE_PAD_X + inset;
    const titleX = design === "card" ? sx + colW / 2 : sx + padX;
    const titleAnchor = design === "card" ? "middle" : "start";
    const headerRuns = parseNoteBlocks(section.header).flat();
    const headerStyled = headerRuns.some((run) => run.bold || run.italic || run.underline);
    chunks.push(`<text x="${round(titleX)}" y="${round(sy + headerH / 2 + 5)}" text-anchor="${titleAnchor}" font-family="Segoe UI, system-ui, sans-serif" font-size="14" font-weight="700" fill="${escapeXml(titleFill)}" style="text-anchor:${titleAnchor};${TEXT_LOCK}">${headerStyled ? runsMarkup(headerRuns) : escapeXml(section.header)}</text>`);
    const bodyInk = textColorForFill(paper);
    if (design === "plain") {
      chunks.push(`<line x1="${round(sx + 8)}" y1="${round(sy + headerH)}" x2="${round(sx + colW - 8)}" y2="${round(sy + headerH)}" stroke="rgba(15,23,42,0.08)" stroke-width="1"/>`);
    }
    const bodyWidth = Math.max(40, colW - padX - NOTE_PAD_X);
    wrapStyledBlocks(parseNoteBlocks(section.body), bodyWidth, NOTE_BODY_SIZE).forEach((line, lineIndex) => {
      const ty = sy + headerH + NOTE_PAD_TOP + NOTE_BODY_SIZE + lineIndex * NOTE_LINE;
      if (!line.length || ty > sy + colH - 4) return;
      chunks.push(`<text x="${round(sx + padX)}" y="${round(ty)}" text-anchor="start" font-family="Segoe UI, system-ui, sans-serif" font-size="${NOTE_BODY_SIZE}" fill="${escapeXml(bodyInk)}" style="text-anchor:start;${TEXT_LOCK}">${runsMarkup(line)}</text>`);
    });
    if (index > 0 && flow === "vertical") {
      chunks.push(`<line x1="${round(sx)}" y1="${round(sy)}" x2="${round(sx + colW)}" y2="${round(sy)}" stroke="#9ca3af" stroke-width="1"/>`);
    }
    if (index > 0 && flow === "horizontal") {
      chunks.push(`<line x1="${round(sx)}" y1="${round(sy)}" x2="${round(sx)}" y2="${round(sy + colH)}" stroke="#9ca3af" stroke-width="1"/>`);
    }
  });
  const frame = sections[0]?.headerFill || "#6b7280";
  const radius = design === "note" ? 4 : 10;
  const stroke = design === "note" ? "none" : design === "outline" ? escapeXml(frame) : "#9ca3af";
  const strokeWidth = design === "outline" ? 2 : 1;
  const fold = design === "note"
    ? `<polygon points="${round(note.x + note.w - 18)},${round(note.y)} ${round(note.x + note.w)},${round(note.y)} ${round(note.x + note.w)},${round(note.y + 18)}" fill="rgba(255,255,255,0.72)"/>`
    : "";
  return `<g clip-path="url(#${clipId})">${chunks.join("")}${fold}</g><rect x="${round(note.x)}" y="${round(note.y)}" width="${round(note.w)}" height="${round(note.h)}" rx="${radius}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
}

function dashArray(pattern, thickness) {
  const weight = Number(thickness) || 1.5;
  if (pattern === "dashed") return `${Math.max(7, weight * 4)} ${Math.max(4, weight * 2.4)}`;
  if (pattern === "dotted") return `${Math.max(1.25, weight * 0.7)} ${Math.max(3.5, weight * 2)}`;
  return "";
}

function shapeMarkup(node) {
  const fill = escapeXml(node.fill || "#ffffff");
  const stroke = escapeXml(node.borderColor || "#334155");
  const sw = node.borderWidth || 1.5;
  const attrs = `fill="${fill}" stroke="${stroke}" stroke-width="${sw}"`;
  const { x, y, w, h, shape } = node;
  if (shape === "rectangle") return `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" ${attrs}/>`;
  if (shape === "circle") {
    return `<ellipse cx="${round(x + w / 2)}" cy="${round(y + h / 2)}" rx="${round(w / 2)}" ry="${round(h / 2)}" ${attrs}/>`;
  }
  if (shape === "triangle") {
    return `<polygon points="${round(x + w / 2)},${round(y)} ${round(x + w)},${round(y + h)} ${round(x)},${round(y + h)}" ${attrs}/>`;
  }
  if (shape === "rhombus") {
    return `<polygon points="${round(x + w / 2)},${round(y)} ${round(x + w)},${round(y + h / 2)} ${round(x + w / 2)},${round(y + h)} ${round(x)},${round(y + h / 2)}" ${attrs}/>`;
  }
  if (shape === "rhomboid") {
    const skew = node.skew || Math.max(16, Math.round(h * 0.28));
    return `<polygon points="${round(x + skew)},${round(y)} ${round(x + w)},${round(y)} ${round(x + w - skew)},${round(y + h)} ${round(x)},${round(y + h)}" ${attrs}/>`;
  }
  return `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" rx="12" ${attrs}/>`;
}

function textMarkup(node) {
  const lines = node.lines || [];
  if (!lines.length) return "";
  const color = textColorForFill(node.fill);
  const lineH = 18;
  const box = node.textBox || { x: 0, y: 0, w: node.w, h: node.h };
  const blockH = lines.length * lineH;
  const startY = node.y + box.y + (box.h - blockH) / 2 + 13;
  const centerX = node.x + box.x + box.w / 2;
  return lines
    .map((line, index) => {
      const y = startY + index * lineH;
      return `<text x="${round(centerX)}" y="${round(y)}" text-anchor="middle" font-family="Segoe UI, system-ui, sans-serif" font-size="14" fill="${color}" style="text-anchor:middle;${TEXT_LOCK}">${escapeXml(line)}</text>`;
    })
    .join("");
}

const LINE_COLOR = "#64748b";

function headShape(head, place, color) {
  const start = place === "start";
  const stroke = escapeXml(color);
  if (head === "triangle") {
    const points = start ? "13,1.5 1,7 13,12.5" : "1,1.5 13,7 1,12.5";
    return `<polygon points="${points}" fill="#ffffff" stroke="${stroke}" stroke-width="1.4" stroke-linejoin="miter"/>`;
  }
  if (head === "diamond" || head === "filled") {
    const points = start ? "1,7 7,1.5 13,7 7,12.5" : "13,7 7,1.5 1,7 7,12.5";
    const fill = head === "filled" ? stroke : "#ffffff";
    return `<polygon points="${points}" fill="${fill}" stroke="${stroke}" stroke-width="1.4" stroke-linejoin="miter"/>`;
  }
  if (head === "plus") {
    return `<circle cx="7" cy="7" r="5.2" fill="#ffffff" stroke="${stroke}" stroke-width="1.4"/>
      <path d="M7 4.2v5.6M4.2 7h5.6" fill="none" stroke="${stroke}" stroke-width="1.4" stroke-linecap="round"/>`;
  }
  const points = start ? "13,2 2,7 13,12" : "1,2 12,7 1,12";
  return `<polyline points="${points}" fill="none" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`;
}

function markerMarkup(id, head, place, color) {
  const start = place === "start";
  const refX = head === "plus" ? (start ? 1.8 : 12.2) : head === "arrow" ? (start ? 2 : 12) : (start ? 1 : 13);
  return `<marker id="${id}" viewBox="0 0 14 14" refX="${refX}" refY="7" markerWidth="16" markerHeight="16" orient="auto" markerUnits="userSpaceOnUse">${headShape(head, place, color)}</marker>`;
}

function lineMarkup(edge, interactive, markers) {
  const d = edge.points
    .map((point, index) => `${index ? "L" : "M"}${round(point.x)} ${round(point.y)}`)
    .join(" ");
  const dash = dashArray(edge.pattern, edge.thickness);
  const arrow = edge.arrow || "none";
  const color = edge.color || LINE_COLOR;
  const head = ["triangle", "diamond", "filled", "plus", "arrow"].includes(edge.arrowHead) ? edge.arrowHead : "arrow";
  const marker = markers[`${head}:${color}`];
  const start = (arrow === "start" || arrow === "both") && marker ? ` marker-start="url(#${marker.start})"` : "";
  const end = (arrow === "end" || arrow === "both") && marker ? ` marker-end="url(#${marker.end})"` : "";
  const path = `<path d="${d}" fill="none" stroke="${escapeXml(color)}" stroke-width="${edge.thickness || 1.5}" stroke-linecap="round" stroke-linejoin="round"${dash ? ` stroke-dasharray="${dash}"` : ""}${start}${end}/>`;
  if (interactive || !edge.text) return path;
  const width = Math.max(36, textWidth(edge.text) + 16);
  const height = 18;
  const x = edge.mid.x - width / 2;
  const y = edge.mid.y - height / 2;
  return `${path}<rect x="${round(x)}" y="${round(y)}" width="${round(width)}" height="${height}" rx="9" fill="#ffffff" stroke="#e5e7eb"/>
    <text x="${round(edge.mid.x)}" y="${round(edge.mid.y + 4)}" text-anchor="middle" font-family="Segoe UI, system-ui, sans-serif" font-size="12" fill="#334155" style="text-anchor:middle;${TEXT_LOCK}">${escapeXml(edge.text)}</text>`;
}

export function renderFlowSvg(record, options = {}) {
  const laid = options.laid || layoutFlow(record?.layout, record?.document);
  const interactive = Boolean(options.interactive);
  const prefix = `fm${Math.random().toString(36).slice(2, 8)}`;
  const markers = {};
  let defs = "";
  let markerIndex = 0;
  [...laid.treeEdges, ...laid.links].forEach((edge) => {
    if (edge.arrow !== "start" && edge.arrow !== "end" && edge.arrow !== "both") return;
    const head = ["triangle", "diamond", "filled", "plus"].includes(edge.arrowHead) ? edge.arrowHead : "arrow";
    const color = edge.color || LINE_COLOR;
    const key = `${head}:${color}`;
    if (markers[key]) return;
    markers[key] = { start: `${prefix}-m${markerIndex}-s`, end: `${prefix}-m${markerIndex}-e` };
    markerIndex += 1;
    defs += markerMarkup(markers[key].end, head, "end", color);
    defs += markerMarkup(markers[key].start, head, "start", color);
  });
  if (!interactive) {
    (laid.notes || []).forEach((note, index) => {
      const noteRadius = note.design === "note" ? 4 : 10;
      defs += `<clipPath id="${prefix}-note-${index}"><rect x="${round(note.x)}" y="${round(note.y)}" width="${round(note.w)}" height="${round(note.h)}" rx="${noteRadius}"/></clipPath>`;
    });
  }
  const body = [
    defs ? `<defs>${defs}</defs>` : "",
    ...laid.treeEdges.map((edge) => lineMarkup(edge, interactive, markers)),
    ...laid.links.map((edge) => lineMarkup(edge, interactive, markers)),
    ...laid.nodes.map((node) => `${shapeMarkup(node)}${interactive ? "" : textMarkup(node)}`),
    ...(interactive ? [] : (laid.notes || []).map((note, index) => noteMarkup(note, `${prefix}-note-${index}`))),
    ...(interactive ? [] : (laid.labels || []).filter((label) => label.text).map((label) => {
      const width = Math.max(36, textWidth(label.text) + 16);
      const height = 22;
      return `<rect x="${round(label.x)}" y="${round(label.y)}" width="${round(width)}" height="${height}" rx="8" fill="#ffffff" stroke="#e5e7eb"/>
        <text x="${round(label.x + width / 2)}" y="${round(label.y + 15)}" text-anchor="middle" font-family="Segoe UI, system-ui, sans-serif" font-size="12" fill="#334155" style="text-anchor:middle;${TEXT_LOCK}">${escapeXml(label.text)}</text>`;
    })),
  ].join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${laid.width}" height="${laid.height}" viewBox="0 0 ${laid.width} ${laid.height}" role="img" aria-label="Flow diagram">${body}</svg>`;
}
